// safeStorage.js — accès au stockage du navigateur qui ne peut pas faire tomber
// l'application.
//
// POURQUOI CE FICHIER EXISTE
//
// `localStorage` n'échoue pas d'une seule manière, et c'est ce qui a piégé le
// code jusqu'ici. Trois cas, trois symptômes différents :
//
//   1. il **lève** — Safari en navigation privée, quota dépassé, iframe
//      cloisonnée par `sandbox` sans `allow-same-origin` (SecurityError) ;
//   2. il **vaut `null`** — WebView Android dont le stockage est désactivé.
//      Un `try/catch` autour de l'appel suffit, mais encore faut-il en avoir
//      mis un : `localStorage.setItem(...)` sur `null` donne « Cannot read
//      properties of null (reading 'setItem') » ;
//   3. il **accepte l'écriture et perd tout** au rechargement (mode privé de
//      certains navigateurs). Rien à faire, sinon ne pas en dépendre.
//
// Le cas 2 s'est produit quatre fois en production les 11 et 13/08/2026, sur
// /public/:id — le chatbot public, diffusable en iframe et souvent ouvert
// depuis la WebView d'un ENT. L'écriture fautive était du code de débogage
// oublié dans `App.js`, qui tournait à chaque montage, sur toutes les routes.
//
// RÈGLE : aucun accès direct à `localStorage` / `sessionStorage` dans `src/`.
// On passe par `safeLocalStorage` / `safeSessionStorage`, dont l'API est celle
// du Storage natif — `getItem` rend `null` quand la lecture est impossible,
// exactement comme une clé absente, et `setItem` / `removeItem` rendent un
// booléen pour les rares appelants qui veulent savoir.

/**
 * Le stockage est atteint À CHAQUE APPEL, jamais mémorisé : `window.localStorage`
 * peut lever au simple accès à la propriété, et l'autorisation peut changer en
 * cours de session (l'usager débloque les cookies du site).
 */
function resoudre(nom) {
  try {
    const store = typeof window !== 'undefined' ? window[nom] : null;
    // `null` (WebView sans stockage) comme un objet sans `getItem` (bouchon
    // exotique) sont traités pareil : indisponible.
    return store && typeof store.getItem === 'function' ? store : null;
  } catch (e) {
    return null;
  }
}

function fabriquer(nom) {
  return {
    /** @return {string|null} la valeur, ou null si absente OU illisible. */
    getItem(cle) {
      try {
        const store = resoudre(nom);
        return store ? store.getItem(cle) : null;
      } catch (e) {
        return null;
      }
    },

    /** @return {boolean} true si l'écriture a abouti. */
    setItem(cle, valeur) {
      try {
        const store = resoudre(nom);
        if (!store) return false;
        store.setItem(cle, valeur);
        return true;
      } catch (e) {
        return false;
      }
    },

    /** @return {boolean} true si la suppression a abouti. */
    removeItem(cle) {
      try {
        const store = resoudre(nom);
        if (!store) return false;
        store.removeItem(cle);
        return true;
      } catch (e) {
        return false;
      }
    },

    /** Le stockage est-il utilisable ? Pour n'afficher une option que si elle tient. */
    disponible() {
      return resoudre(nom) !== null;
    },
  };
}

export const safeLocalStorage = fabriquer('localStorage');
export const safeSessionStorage = fabriquer('sessionStorage');
